import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../../components/kit/Button';
import { PhoneInput } from '../../components/form/PhoneInput';
import { haptic } from '../../lib/haptic';
import { AuthScreenShell } from '../../components/auth/AuthScreenShell';
import { InstallCard } from '../../components/install/InstallCard';
import { redeemInviteCode } from '../../lib/api-client';
import { isProfileComplete } from '../../lib/profile-completeness';
import { useAuth } from '../../lib/auth';

const MAX_LEN = 12;
const PHONE_RE = /^[6-9]\d{9}$/;

// localStorage key written by prior sessions; read-only here — we don't write
// it on InviteRoute to avoid polluting state for fresh installs.
const STORED_PHONE_KEY = 'last_phone';

export function InviteRoute() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code')?.toUpperCase() ?? '');
  // Prefill phone from a prior session if available
  const [phone, setPhone] = useState(localStorage.getItem(STORED_PHONE_KEY)?.replace(/^\+91/, '') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track the server error CODE so an already-used / invalid link (both return
  // INVALID_CODE — codes are single-use) can show a recovery helper (#501).
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const nav = useNavigate();
  const setAuth = useAuth.setState;

  const phoneValid = PHONE_RE.test(phone);

  // Magic-link auto-submit: only fire if BOTH code AND phone are already known.
  // If phone isn't stored, we leave the code prefilled and wait for the user to
  // type their number — the redeemCode endpoint now requires both.
  useEffect(() => {
    const fromUrl = params.get('code');
    if (!fromUrl) return;
    const clean = fromUrl.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!/^[A-Z0-9]{4,12}$/.test(clean)) return;
    setCode(clean);
    const storedPhone = localStorage.getItem(STORED_PHONE_KEY)?.replace(/^\+91/, '') ?? '';
    if (PHONE_RE.test(storedPhone)) {
      void onSubmit(storedPhone, clean);
    }
    // else: show the phone field with code pre-filled, wait for user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = code.trim();
  const canContinue = trimmed.length > 0 && phoneValid && !submitting;

  async function onSubmit(rawPhone: string, rawCode: string) {
    const value = rawCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!value || !PHONE_RE.test(rawPhone)) { haptic.warn(); return; }
    setError(null);
    setErrorCode(null);
    setSubmitting(true);
    haptic.select();
    try {
      const { driver } = await redeemInviteCode(`+91${rawPhone}`, value);
      // Update the auth store so the app immediately sees the authenticated driver
      // without a round-trip to /me. The backend has already set the auth cookie.
      setAuth({ status: 'authenticated', driver, pending: null, error: null });
      if (isProfileComplete(driver)) {
        nav('/', { replace: true });
      } else {
        // Profile is incomplete — the operator may have pre-filled name/branch
        // in the invite, so pass whatever we have as prefill for the wizard.
        nav('/auth/signup', { replace: true, state: { prefill: driver } });
      }
    } catch (e: any) {
      // Surface the server's error code so the driver can relay it to their operator.
      const errCode: string = e?.code ?? 'unknown';
      setErrorCode(errCode);
      setError(
        errCode === 'INVALID_CODE'
          ? t('inviteInvalidCode')
          : t('inviteGenericError', { code: errCode }),
      );
      haptic.warn();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreenShell
      anchor={t('signupAnchor')}
      actionVerb={t('signupActionVerb')}
      subhead={t('inviteSubhead')}
      footer={
        <Link
          to="/auth/phone"
          style={{
            color: 'var(--green-700)',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {t('inviteAlreadyHaveAccount')}
        </Link>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void onSubmit(phone, trimmed); }}>
        {/* Phone field — required since redeemCode now needs both phone + code */}
        <label
          className="block"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text)',
            marginBottom: '8px',
          }}
        >
          {t('mobileNumber')}
        </label>
        <PhoneInput value={phone} onChange={setPhone} autoFocus={!phone} />

        <label
          className="block"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text)',
            marginTop: '16px',
            marginBottom: '8px',
          }}
        >
          {t('invitePlaceholder')}
        </label>
        <input
          autoFocus={!!phone}
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={MAX_LEN}
          value={code}
          onChange={(e) => {
            // Alphanumeric only; auto-uppercase for visual consistency with
            // typical invite-code formats (e.g. FU-ABC123).
            const next = e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase().slice(0, MAX_LEN);
            setCode(next);
          }}
          placeholder={t('invitePlaceholder')}
          className="w-full border border-[var(--color-border)] rounded-[var(--radius-md)] bg-white px-3 py-3 text-base outline-none tracking-wider"
          style={{ fontFamily: 'inherit', letterSpacing: '0.08em' }}
        />
        {errorCode === 'INVALID_CODE' ? (
          // Single-use link already redeemed (or a typo). Point the driver at
          // the real recovery — OTP sign-in for an already-activated phone —
          // and offer to install the app so they stop reopening the link (#501).
          <div
            className="rounded-2xl p-4 mt-3"
            style={{ background: 'var(--status-warning-bg,#FBF1DE)', border: '1px solid rgba(197, 142, 58, 0.35)' }}
          >
            <div className="text-sm font-bold text-[var(--color-text)] mb-1">
              {t('inviteUsedTitle', { defaultValue: "This link didn't work" })}
            </div>
            <div className="text-xs text-[var(--color-text-secondary)] mb-3 leading-relaxed">
              {t('inviteUsedBody', {
                defaultValue:
                  "Invite links work only once. If FleetUp is already set up on this phone, sign in with your number instead — you don't need a new link. Ask your operator for a new link only if you've never signed in.",
              })}
            </div>
            <Link
              to="/auth/phone"
              onClick={() => haptic.tap()}
              className="inline-flex items-center justify-center w-full py-2.5 rounded-full text-sm font-bold"
              style={{ background: 'var(--color-btn-primary)', color: '#fff', textDecoration: 'none' }}
            >
              {t('inviteSignInOtp', { defaultValue: 'Sign in with OTP' })}
            </Link>
            <div className="mt-3">
              <InstallCard />
            </div>
          </div>
        ) : error ? (
          <p style={{ color: 'var(--red-600, #dc2626)', marginTop: 8, fontSize: 14 }}>{error}</p>
        ) : null}
        <div style={{ marginTop: '22px' }}>
          <Button type="submit" disabled={!canContinue}>
            {submitting ? t('inviteSubmitting') : t('inviteContinue')}
          </Button>
        </div>
      </form>
    </AuthScreenShell>
  );
}
